import { useState, useMemo, useCallback } from 'react';

const fmt = (n: number) => `${Math.round(n).toLocaleString('pl-PL')} zł`;

const annuity = (K: number, r: number, n: number): number => {
  const m = r / 1200;
  if (m === 0) return K / n;
  return (K * m) / (1 - Math.pow(1 + m, -n));
};

interface SimResult {
  months: number;
  interest: number;
  balances: number[];
}

const simulate = (K: number, r: number, n: number, pay: number, extraMonthly: number): SimResult | null => {
  const m = r / 1200;
  let bal = K;
  let interest = 0;
  let months = 0;
  const balances = [bal];
  while (bal > 0.01 && months < n * 2 + 600) {
    const int = bal * m;
    interest += int;
    let principal = pay + extraMonthly - int;
    if (principal <= 0) return null;
    if (principal > bal) principal = bal;
    bal -= principal;
    months++;
    balances.push(bal);
  }
  return { months, interest, balances };
};

type Mode = 'term' | 'rata';

function SliderField({
  label,
  value,
  display,
  min,
  max,
  step,
  marks,
  onChange,
}: {
  label: string;
  value: number;
  display: React.ReactNode;
  min: number;
  max: number;
  step: number;
  marks: [string, string, string];
  onChange: (v: number) => void;
}) {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider">{label}</span>
        <span className="text-sm font-extrabold text-foreground-950 tracking-tight">{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
      />
      <div className="flex justify-between text-[10px] font-semibold text-foreground-400 mt-1">
        <span>{marks[0]}</span>
        <span>{marks[1]}</span>
        <span>{marks[2]}</span>
      </div>
    </div>
  );
}

export default function OverpaymentCalculator() {
  const [balance, setBalance] = useState(400000);
  const [years, setYears] = useState(25);
  const [rate, setRate] = useState(6.5);
  const [extraMonthly, setExtraMonthly] = useState(500);
  const [extraOnce, setExtraOnce] = useState(0);
  const [mode, setMode] = useState<Mode>('term');

  const n = years * 12;

  const { base, withOv, saved, fasterM, newRata, K1, baseRata } = useMemo(() => {
    const baseRata = annuity(balance, rate, n);
    const base = simulate(balance, rate, n, baseRata, 0);
    const K1 = Math.max(0, balance - extraOnce);

    let withOv: SimResult | null = null;
    let newRata = 0;
    let fasterM = 0;

    if (mode === 'term') {
      withOv = K1 > 0 ? simulate(K1, rate, n, baseRata, extraMonthly) : { months: 0, interest: 0, balances: [0] };
      fasterM = base ? base.months - withOv.months : 0;
    } else {
      newRata = K1 > 0 ? annuity(K1, rate, n) : 0;
      withOv = K1 > 0 ? simulate(K1, rate, n, annuity(K1, rate, n), extraMonthly) : { months: 0, interest: 0, balances: [0] };
      fasterM = base ? base.months - withOv.months : 0;
    }

    const saved = base ? base.interest - withOv.interest : 0;

    return { base, withOv, saved, fasterM, newRata, K1, baseRata };
  }, [balance, rate, n, extraMonthly, extraOnce, mode]);

  const yF = Math.floor(fasterM / 12);
  const mF = fasterM % 12;

  const hintText = useMemo(() => {
    if (saved > 50000) {
      return (
        <>
          Oszczędność <b>{fmt(saved)}</b> robi ogromną różnicę. Ekspert kredytowy sprawdzi też, czy połączenie nadpłaty z refinansowaniem nie da jeszcze więcej.
        </>
      );
    }
    if (saved > 0) {
      return (
        <>
          Każda nadpłata pracuje na Twoją korzyść. Warto porównać ten scenariusz z refinansowaniem — ekspert kredytowy policzy oba warianty bezpłatnie.
        </>
      );
    }
    return (
      <>
        Ustaw kwotę nadpłaty miesięcznej lub jednorazowej, aby zobaczyć oszczędność. W razie wątpliwości ekspert kredytowy podpowie optymalną strategię.
      </>
    );
  }, [saved]);

  const drawChart = useCallback(() => {
    if (!base || !withOv) return null;
    const W = 460;
    const H = 180;
    const padB = 16;
    const padT = 6;
    const padL = 4;
    const sx = (W - padL * 2) / base.months;
    const sy = (H - padT - padB) / balance;

    const makePath = (arr: number[], step: number) => {
      let d = `M ${padL} ${(padT + (balance - arr[0]) * sy).toFixed(1)}`;
      for (let i = 1; i < arr.length; i += step) {
        const idx = Math.min(i, arr.length - 1);
        d += ` L ${(padL + i * sx).toFixed(1)} ${(padT + (balance - arr[idx]) * sy).toFixed(1)}`;
      }
      d += ` L ${(padL + (arr.length - 1) * sx).toFixed(1)} ${H - padB}`;
      return d;
    };

    const step = Math.max(1, Math.floor(base.months / 140));
    const yrs = Math.round(base.months / 12);

    let ticks = '';
    for (const fr of [0, 0.5, 1]) {
      ticks += `<text x="${padL + (W - padL * 2) * fr}" y="${H - 2}" font-size="9" fill="#94a3b8" text-anchor="middle">${Math.round(yrs * fr)} lat</text>`;
    }

    return (
      <svg viewBox="0 0 460 180" preserveAspectRatio="none" className="w-full h-[180px] mt-3">
        <line x1={padL} y1={H - padB} x2={W - padL} y2={H - padB} stroke="#e2e8f0" />
        <path d={makePath(base.balances, step)} fill="none" stroke="#cbd5e1" strokeWidth="2.5" />
        <path d={makePath(withOv.balances, step)} fill="none" stroke="#059669" strokeWidth="2.5" />
        <g dangerouslySetInnerHTML={{ __html: ticks }} />
      </svg>
    );
  }, [base, withOv, balance]);

  return (
    <div>
      {/* Hero */}
      <section className="bg-foreground-950 text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary-400 mb-3 opacity-0 animate-slide-up animation-delay-100">
            Kalkulator · Nadpłata kredytu
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mx-auto max-w-2xl text-center leading-tight mb-4 opacity-0 animate-slide-up animation-delay-200">
            Ile lat i tysięcy złotych ucina regularna nadpłata?
          </h1>
          <p className="text-sm md:text-base text-white/60 mx-auto max-w-xl text-center leading-relaxed opacity-0 animate-slide-up animation-delay-300">
            Nawet niewielka comiesięczna nadpłata potrafi skrócić kredyt o kilka lat i obniżyć odsetki o dziesiątki tysięcy złotych. Sprawdź dokładnie, ile zyskasz.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 items-start">
            {/* Inputs */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Twój kredyt i nadpłata
              </h3>

              <SliderField
                label="Pozostało do spłaty"
                value={balance}
                display={
                  <>
                    {balance.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={50000}
                max={1500000}
                step={10000}
                marks={['50 tys.', '750 tys.', '1,5 mln']}
                onChange={setBalance}
              />

              <SliderField
                label="Pozostały okres spłaty"
                value={years}
                display={
                  <>
                    {years}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">
                      {years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}
                    </span>
                  </>
                }
                min={1}
                max={35}
                step={1}
                marks={['1', '17', '35']}
                onChange={setYears}
              />

              <SliderField
                label="Oprocentowanie"
                value={rate}
                display={
                  <>
                    {rate.toFixed(2).replace('.', ',')}
                    <span className="text-foreground-400 font-semibold text-xs">%</span>
                  </>
                }
                min={3}
                max={12}
                step={0.05}
                marks={['3%', '7,5%', '12%']}
                onChange={setRate}
              />

              <SliderField
                label="Nadpłata miesięczna"
                value={extraMonthly}
                display={
                  <>
                    {extraMonthly.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={0}
                max={5000}
                step={50}
                marks={['0', '2,5 tys.', '5 tys.']}
                onChange={setExtraMonthly}
              />

              <SliderField
                label="Nadpłata jednorazowa (teraz)"
                value={extraOnce}
                display={
                  <>
                    {extraOnce.toLocaleString('pl-PL')}{' '}
                    <span className="text-foreground-400 font-semibold text-xs">zł</span>
                  </>
                }
                min={0}
                max={300000}
                step={5000}
                marks={['0', '150 tys.', '300 tys.']}
                onChange={setExtraOnce}
              />

              <div className="mb-0">
                <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-wider block mb-2">
                  Efekt nadpłaty
                </span>
                <div className="grid grid-cols-2 border-[1.5px] border-background-200 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setMode('term')}
                    className={`py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                      mode === 'term'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-foreground-500 hover:bg-background-100'
                    }`}
                  >
                    Skróć okres
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('rata')}
                    className={`py-2.5 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                      mode === 'rata'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-foreground-500 hover:bg-background-100'
                    }`}
                  >
                    Obniż ratę
                  </button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6 shadow-sm">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-5">
                Twój zysk z nadpłaty
              </h3>

              <div className="bg-green-700 rounded-xl text-white p-6 text-center">
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-green-300">
                  Oszczędność na odsetkach
                </div>
                <div className="text-2xl md:text-3xl font-extrabold tracking-tight mt-1">
                  {fmt(saved)}
                </div>
                <div className="text-sm text-green-200 mt-1">
                  {extraMonthly === 0 && extraOnce === 0
                    ? 'ustaw nadpłatę, aby zobaczyć efekt'
                    : `przy spłacie szybszej o ${yF > 0 ? `${yF} lat ` : ''}${mF > 0 ? `${mF} mies.` : yF === 0 ? '0 mies.' : ''}`}
                </div>
              </div>

              <div className="mt-4">
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Rata bez nadpłaty</span>
                  <b className="text-foreground-900">{fmt(baseRata)}</b>
                </div>
                {mode === 'term' && (
                  <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                    <span className="text-foreground-500">Spłacisz szybciej o</span>
                    <b className="text-foreground-900">
                      {yF > 0 || mF > 0 ? `${yF > 0 ? `${yF} lat ` : ''}${mF > 0 ? `${mF} mies.` : ''}` : '—'}
                    </b>
                  </div>
                )}
                {mode === 'rata' && (
                  <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                    <span className="text-foreground-500">Nowa rata po nadpłacie</span>
                    <b className="text-foreground-900">{K1 > 0 ? fmt(newRata) : '—'}</b>
                  </div>
                )}
                <div className="flex justify-between py-2.5 border-b border-background-100 text-sm">
                  <span className="text-foreground-500">Odsetki bez nadpłaty</span>
                  <b className="text-foreground-900">{base ? fmt(base.interest) : '—'}</b>
                </div>
                <div className="flex justify-between py-2.5 text-sm">
                  <span className="text-foreground-500">Odsetki z nadpłatą</span>
                  <b className="text-foreground-900">{fmt(withOv.interest)}</b>
                </div>
              </div>

              {drawChart()}
              <div className="flex gap-5 mt-2 text-xs font-semibold text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-background-300 inline-block" />
                  Saldo bez nadpłaty
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-green-600 inline-block" />
                  Saldo z nadpłatą
                </span>
              </div>

              <div className="bg-primary-100 border border-primary-200 rounded-xl p-4 mt-5 text-sm text-primary-800 leading-relaxed">
                {hintText}
              </div>

              <p className="text-[11px] text-foreground-400 leading-relaxed mt-4">
                Pełna miesięczna symulacja harmonogramu rat równych. Od 2022 r. banki nie mogą pobierać opłat za nadpłatę kredytu hipotecznego o zmiennym oprocentowaniu po 3 latach od uruchomienia (przy stałym mogą obowiązywać opłaty — sprawdź umowę).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3">
                Nadpłacać czy inwestować? A może refinansować i nadpłacać?
              </h2>
              <p className="text-sm text-white/60 max-w-xl mx-auto mb-8 leading-relaxed">
                Najlepsza strategia zależy od Twojego oprocentowania, podatków i planów. Ekspert kredytowy bezpłatnie policzy scenariusze na Twoich liczbach i pomoże wybrać najkorzystniejszy.
              </p>
              <a
                href="tel:698084044"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-white font-extrabold text-lg rounded-full hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg shadow-primary-500/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" />
                698 084 044
              </a>
              <div className="flex flex-wrap gap-5 justify-center mt-6">
                {[
                  'Bezpłatna analiza scenariuszy',
                  'Strategia dopasowana do Ciebie',
                  'Bez zobowiązań',
                ].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-xs text-white/50">
                    <i className="ri-check-line text-green-400" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="pb-10 md:pb-14 bg-background-50">
        <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
          <p className="text-[11px] text-foreground-400 leading-relaxed text-center max-w-3xl mx-auto">
            Kalkulator ma charakter poglądowy i edukacyjny — nie stanowi porady inwestycyjnej ani finansowej. Warunki wcześniejszej spłaty i nadpłat określa Twoja umowa kredytowa.
          </p>
        </div>
      </section>
    </div>
  );
}